import { useMemo } from 'react';

import { LANGUAGE_CODES, getLanguageDisplayName } from '@/i18n/languages';

interface SupportedLanguage {
  code: string;
  name: string;
}

/**
 * Hook to get supported languages from the shipped locale files.
 *
 * Codes come from `LANGUAGE_CODES` (filesystem enumeration), not from
 * i18n.store.data — so the switcher lists every language even before its
 * bundle has been loaded into memory.
 */
export const useSupportedLanguages = () => {
  const { languages, languageCodes } = useMemo(() => {
    const languages: SupportedLanguage[] = LANGUAGE_CODES.map((code) => ({
      code,
      name: getLanguageDisplayName(code),
    }));

    return {
      languages,
      languageCodes: [...LANGUAGE_CODES],
    };
  }, []);

  return {
    languages,
    languageCodes,
    isLoading: false,
    error: null,
  };
};

/**
 * Simple hook that just returns the language codes.
 * Useful for validation without needing the full language objects.
 */
export const useSupportedLanguageCodes = (): string[] => {
  const { languageCodes } = useSupportedLanguages();
  return languageCodes;
};
